import BigNumber from 'bignumber.js'

import { Duration } from '@blade/std-lib'
import { WalletAddress } from '@root/entities/wallet'
import {
  UserTradingAnalyticsMcDistributionThreshold,
  type UserTradingAnalyticsMcDistribution,
} from '@root/features/user/user-trading-analytics/models/user-trading-analytics-mc-distribution'
import {
  UserTradingAnalyticsPhishingPoint,
  UserTradingAnalyticsPhishingSeverity,
  type UserTradingAnalyticsPhishing,
} from '@root/features/user/user-trading-analytics/models/user-trading-analytics-phishing'

import type * as WalletProfile from './wallet-profile'
import type { Network } from '@root/entities/network'
import type { UserTradingAnalytics } from '@root/features/user/user-trading-analytics/models/user-trading-analytics'

/**
 * One wallet's figures, in the shape the trading dashboard already draws.
 *
 * The terminal carries a whole analytics screen ported from deluge — the PnL
 * chart, the calendar, the distribution bars, the analysis grid — whose
 * backend was never ported. Every one of its components takes its data as a
 * prop, so they do not need rewriting, they need feeding.
 *
 * The two shapes agree more than they differ. The distribution is the same
 * five buckets to the name; the daily series is the same day, PnL and
 * volumes; the totals are the same totals. What follows is mostly renaming,
 * and the places it is not are marked.
 */

/** Dollars, and the same figure in the native asset at `solPrice`. */
const value = (usd: number, solPrice: number) => ({
  usd: BigNumber(usd),
  native: BigNumber(solPrice > 0 ? usd / solPrice : 0),
})

const bucket = (count: number, total: number) => ({
  count,
  percent: total === 0 ? 0 : (count / total) * 100,
})

/**
 * Nothing here scores a wallet for phishing.
 *
 * The deluge backend judged four things — a blacklist, buying nothing,
 * selling more than was bought, flipping within seconds — and none of them
 * can be answered from what this reads. Reporting zeros would say "we
 * checked, it is clean", which is a different claim from "we did not check",
 * so the block that draws this is left out of the wallet screen entirely
 * rather than shown empty.
 */
const NOTHING_CHECKED: UserTradingAnalyticsPhishing = Object.fromEntries(
  Object.values(UserTradingAnalyticsPhishingPoint).map((point) => [
    point,
    {
      count: 0,
      percent: 0,
      severity: UserTradingAnalyticsPhishingSeverity.Normal,
    },
  ]),
) as UserTradingAnalyticsPhishing

const marketCaps = (
  caps: WalletProfile.EntryMarketCaps,
): UserTradingAnalyticsMcDistribution => {
  // The unknown ones are left out of the total rather than counted as a
  // band, so the percentages describe the positions we can place.
  const placed = caps.below100k + caps.from100kTo500k + caps.above500k
  return {
    [UserTradingAnalyticsMcDistributionThreshold.Below100k]: bucket(
      caps.below100k,
      placed,
    ),
    [UserTradingAnalyticsMcDistributionThreshold.From100kTo500k]: bucket(
      caps.from100kTo500k,
      placed,
    ),
    [UserTradingAnalyticsMcDistributionThreshold.Above500k]: bucket(
      caps.above500k,
      placed,
    ),
  }
}

export const toTradingAnalytics = (
  profile: WalletProfile.WalletProfile,
  network: Network.Network,
  /** What the native asset is worth, for the figures the screen shows twice. */
  solPrice: number,
): UserTradingAnalytics => {
  const { totals, distribution, positions } = profile
  const closed = totals.closedPositions

  return {
    walletAddresses: [WalletAddress(profile.walletAddress)],
    network,
    phishing: NOTHING_CHECKED,
    mcDistribution: marketCaps(profile.entryMarketCaps),
    performance: {
      totalPnlUsd: BigNumber(totals.totalPnl),
      realizedPnlUsd: BigNumber(totals.realizedPnl),
      unrealizedPnlUsd: BigNumber(totals.unrealizedPnl),
      totalPnlNative: value(totals.totalPnl, solPrice).native,
      realizedPnlNative: value(totals.realizedPnl, solPrice).native,
      unrealizedPnlNative: value(totals.unrealizedPnl, solPrice).native,
      tradedPairs: totals.tokensTraded,
      closedPairs: closed,
      winningClosedPairs: totals.winningClosedPositions,
      winRate:
        closed === 0 ? 0 : (totals.winningClosedPositions / closed) * 100,
      avgHoldSeconds: totals.avgHoldSeconds ?? 0,
      pnlDistribution: {
        over500: distribution.gainOver500,
        from200to500: distribution.gain200To500,
        from0to200: distribution.gain0To200,
        from0toNeg50: distribution.loss0To50,
        belowNeg50: distribution.lossOver50,
      },
      // Nothing here compares a window against the one before it.
      totalPnlUsdChangePercent: undefined,
      realizedPnlUsdChangePercent: undefined,
      unrealizedPnlUsdChangePercent: undefined,
      winRateChangePercent: undefined,
    },
    analysis: {
      txs: { buys: totals.buys, sells: totals.sells },
      avgHold: Duration.seconds(totals.avgHoldSeconds ?? 0),
      balance: value(
        positions.reduce(
          (total, row) => total + row.remainingTokens * row.priceUsd,
          0,
        ),
        solPrice,
      ),
      cost: value(totals.spentUsd, solPrice),
      avgCost: value(
        totals.tokensTraded === 0 ? 0 : totals.spentUsd / totals.tokensTraded,
        solPrice,
      ),
      avgSold: value(
        totals.tokensTraded === 0
          ? 0
          : totals.receivedUsd / totals.tokensTraded,
        solPrice,
      ),
      avgRealizedProfits: value(
        closed === 0 ? 0 : totals.realizedPnl / closed,
        solPrice,
      ),
      // The swap fee a pool charged is not in what this reads; it is inside
      // the price each trade went off at and never recorded apart from it.
      fees: value(0, solPrice),
      volume: value(totals.spentUsd + totals.receivedUsd, solPrice),
    },
    daily: profile.realizedPnlSeries.map((point) => ({
      date: new Date(point.day),
      buyVolumeUsd: BigNumber(point.buyVolumeUsd),
      sellVolumeUsd: BigNumber(point.sellVolumeUsd),
      // The series is cumulative, which is what the chart draws.
      pnlUsd: BigNumber(point.realizedPnl),
      realizedPnlUsd: BigNumber(point.realizedPnl),
      buys: point.buys,
      sells: point.sells,
    })),
  }
}
